import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, DollarSign, BarChart3, BookOpen, Trophy, Gamepad2 } from 'lucide-react';

export default function DashboardHome() {
  const [stats, setStats] = useState({
    totalHosts: 0,
    activeGamesToday: 0,
    monthlyRevenue: 0,
    totalGames: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // TODO: Replace with actual API call when endpoint is ready
      // const response = await analyticsAPI.getOverview();
      // setStats(response.data);
      
      // Placeholder data for now
      setTimeout(() => {
        setStats({
          totalHosts: 1,
          activeGamesToday: 0,
          monthlyRevenue: 49.99,
          totalGames: 0,
        });
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Platform Overview</h1>
        <p className="text-gray-600 mt-2">Welcome to the Quizzler Admin Dashboard</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Hosts */}
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Total Hosts</h3>
            <Users size={32} className="text-slate-600" />
          </div>
          <p className="text-4xl font-bold text-slate-600">{stats.totalHosts}</p>
          <Link to="/dashboard/hosts" className="text-sm text-slate-600 hover:underline mt-2 inline-block">
            View All →
          </Link>
        </div>

        {/* Active Games Today */}
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Active Games Today</h3>
            <Gamepad2 size={32} className="text-teal-600" />
          </div>
          <p className="text-4xl font-bold text-azure-600">{stats.activeGamesToday}</p>
          <p className="text-sm text-gray-500 mt-2">Live trivia sessions</p>
        </div>

        {/* Monthly Revenue */}
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Monthly Revenue</h3>
            <DollarSign size={32} className="text-slate-600" />
          </div>
          <p className="text-4xl font-bold text-green-600">${stats.monthlyRevenue}</p>
          <Link to="/dashboard/subscriptions" className="text-sm text-green-600 hover:underline mt-2 inline-block">
            View Details →
          </Link>
        </div>

        {/* Total Games Played */}
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Total Games</h3>
            <BarChart3 size={32} className="text-slate-600" />
          </div>
          <p className="text-4xl font-bold text-orange-600">{stats.totalGames}</p>
          <p className="text-sm text-gray-500 mt-2">All time</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Link
          to="/dashboard/templates"
          className="bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white rounded-xl p-6 transition shadow-md"
        >
          <div className="mb-3"><BookOpen size={32} className="text-slate-600" /></div>
          <h3 className="text-xl font-bold mb-2">Create Game Template</h3>
          <p className="text-slate-100">Add new official trivia games</p>
        </Link>

        <Link
          to="/dashboard/hosts"
          className="bg-gradient-to-r from-azure-500 to-azure-600 hover:from-azure-600 hover:to-azure-700 text-white rounded-xl p-6 transition shadow-md"
        >
          <div className="mb-3"><Users size={32} className="text-slate-600" /></div>
          <h3 className="text-xl font-bold mb-2">Manage Hosts</h3>
          <p className="text-azure-100">View and edit host accounts</p>
        </Link>

        <Link
          to="/dashboard/championships"
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl p-6 transition shadow-md"
        >
          <div className="mb-3"><Trophy size={32} className="text-slate-600" /></div>
          <h3 className="text-xl font-bold mb-2">Championships</h3>
          <p className="text-orange-100">View top teams by region</p>
        </Link>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Activity</h2>
        <div className="text-center py-8 text-gray-500">
          <p>No recent activity</p>
          <p className="text-sm mt-2">Activity will appear here as hosts use the platform</p>
        </div>
      </div>
    </div>
  );
}
